import classNames from 'classnames';
import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../../components/Button/LoadingSpinner/LoadingSpinner';
import DashboardCard from '../../../components/DashboardCard/DashboardCard';
import Icon from '../../../components/Icons/Icon';
import SummaryBlock from '../../../components/SummaryBlock/SummaryBlock';
import { SummaryBlockVariants } from '../../../components/SummaryBlock/SummaryBlock.types';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { useBundles } from '../../../services/bundles/bundles.service';
import { usePromotions } from '../../../services/promotions/promotions.service';
import { useSubmissions } from '../../../services/submissions/submissions.service';
import { FzSubmissionSummary, Submission, SubmissionStatus, SubmissionSubType } from '../../../types/submission.types';
import useFranchiseeAlias from '../../../utils/useFranchiseeAlias/useFranchiseeAlias';
import styles from './Dashboard.module.css';
import { useTranslation } from 'react-i18next';

const summarizeSubmissions = (data: Submission[]) =>
  data.reduce<FzSubmissionSummary>(
    (previousValue: FzSubmissionSummary, currentValue: Submission) => {
      const summaries = { ...previousValue };
      if (currentValue.status === SubmissionStatus.PENDING) {
        switch (currentValue.subType) {
          case SubmissionSubType.BUNDLE_SETTING_CHANGE:
            summaries.pendingBundleSettingCount += 1;
            break;
          case SubmissionSubType.DEACTIVATE_BUNDLE:
            summaries.pendingDeactivateCount += 1;
            break;
          case SubmissionSubType.NEW_BUNDLE_GROUP:
            summaries.pendingNewGroupCount += 1;
            break;
          case SubmissionSubType.EDIT_BUNDLE_GROUP:
            summaries.pendingEditGroupCount += 1;
            break;
          default:
            break;
        }
      } else {
        switch (currentValue.subType) {
          case SubmissionSubType.BUNDLE_SETTING_CHANGE:
            summaries.completedBundleSettingCount += 1;
            break;
          case SubmissionSubType.DEACTIVATE_BUNDLE:
            summaries.completedDeactivateCount += 1;
            break;
          case SubmissionSubType.NEW_BUNDLE_GROUP:
            summaries.completedNewGroupCount += 1;
            break;
          case SubmissionSubType.EDIT_BUNDLE_GROUP:
            summaries.completedEditGroupCount += 1;
            break;
          default:
            break;
        }
      }
      return summaries;
    },
    {
      pendingBundleSettingCount: 0,
      pendingDeactivateCount: 0,
      pendingNewGroupCount: 0,
      pendingEditGroupCount: 0,
      completedBundleSettingCount: 0,
      completedDeactivateCount: 0,
      completedNewGroupCount: 0,
      completedEditGroupCount: 0,
    },
  );

const Dashboard: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { alias } = useFranchiseeAlias();
  const { data: bundleData, isError: bundleIsError } = useBundles(true);
  const { data: promoData, isError: promoIsError } = usePromotions(true);
  const { data } = useSubmissions(true);
  const submissions = data?.data || [];
  const {
    pendingBundleSettingCount,
    pendingDeactivateCount,
    pendingNewGroupCount,
    pendingEditGroupCount,
    completedBundleSettingCount,
    completedDeactivateCount,
    completedNewGroupCount,
    completedEditGroupCount,
  } = summarizeSubmissions(submissions);

  const handleHelpClick = () => navigate('/');
  const handleUserClick = () => navigate('/account/profile');
  const handleBundlesClick = () => navigate('/bundles/active');
  const handleSubmissionsClick = () => navigate('/submissions');

  useEffect(() => {
    if (bundleIsError)
      showToast({ toastType: ToastTypes.Error, label: t('error.fetchingBundles'), id: 'bundleFetchError' });
  }, [bundleIsError]);

  useEffect(() => {
    if (promoIsError)
      showToast({ toastType: ToastTypes.Error, label: t('error.fetchingPromotions'), id: 'promoFetchError' });
  }, [promoIsError]);

  const promoCount = promoData?.data.length !== undefined ? promoData?.data.length : <LoadingSpinner />;
  const bundleCount = bundleData?.data.length !== undefined ? bundleData?.data.length : <LoadingSpinner />;

  localStorage.removeItem('STK_DATA');
  return (
    <div>
      {alias === null && <div className={styles.banner} />}
      <div className={styles.header}>
        <h1>{t('common.welcomeBack')}</h1>
        <div className={styles.icons}>
          <Icon iconName="HelpCircle" onClick={handleHelpClick} title={t('common.help')} id="helpIcon" />
          <Icon iconName="User" onClick={handleUserClick} title={t('nav.accountProfile')} id="profileIcon" />
        </div>
      </div>
      <div className={styles.page}>
        <div className={styles.totals}>
          <div className={styles.totalMessage}>
            <span className={styles.bold}>{t('dashboard.totalActiveBundles')}:</span>
            <span
              className={classNames(styles.count, { [styles.isLoading]: bundleData?.data.length !== undefined })}
              data-testid="bundleCount"
            >
              {bundleCount}
            </span>
          </div>
          <div className={styles.totalDivider} />
          <div className={styles.totalMessage}>
            <span className={styles.bold}>{t('dashboard.totalActivePromotions')}:</span>
            <span
              className={classNames(styles.count, { [styles.isLoading]: promoData?.data.length !== undefined })}
              data-testid="promoCount"
            >
              {promoCount}
            </span>
          </div>
        </div>

        <div className={styles.cards}>
          <DashboardCard
            title={t('dashboard.submissionsTitle')}
            onViewClick={handleSubmissionsClick}
            buttonAriaLabel={t('dashboard.submissionsViewAriaLabel')}
            className={styles.card}
          >
            <div className={styles.summaryBlocks} data-testid="summaryBlocks">
              <SummaryBlock
                variant={SummaryBlockVariants.LightBlue}
                className={styles.summaryBlockCard}
                blocks={[
                  {
                    label: t('dashboard.submissionsPendingLabel'),
                    count:
                      pendingBundleSettingCount + pendingDeactivateCount + pendingNewGroupCount + pendingEditGroupCount,
                  },
                ]}
              />
              <SummaryBlock
                variant={SummaryBlockVariants.Green}
                className={styles.summaryBlockCard}
                blocks={[
                  {
                    label: t('dashboard.submissionsCompletedLabel'),
                    count:
                      completedBundleSettingCount +
                      completedDeactivateCount +
                      completedNewGroupCount +
                      completedEditGroupCount,
                  },
                ]}
              />
            </div>
          </DashboardCard>
          <DashboardCard
            title={t('dashboard.bundlesTitle')}
            onViewClick={handleBundlesClick}
            titleIcon="Tag"
            buttonAriaLabel={t('dashboard.bundleViewAriaLabel')}
            className={styles.card}
          >
            <div className={styles.bundlesMessage}>{t('dashboard.bundlesDescription')}</div>
          </DashboardCard>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
